import React, { useState, useRef } from "react";
import { EmojiClickData } from "emoji-picker-react";
import { MouseDownEvent } from "emoji-picker-react/dist/config/config";
import {
    Modal,
    Button,
    Dropdown,
    Icon,
    TextArea,
    Tooltip,
    TextField,
} from "ui/components";
import * as facebookConstants from "core/constants/facebook";
import { Fragment, SubtitleExportValue } from "core/models";
import useOnClickOutside from "ui/hooks/useOnClickOutside";
import { subtitleExportOptionsBasic } from "core/constants/captionsExport";
import { usePublishContext } from "ui/context/PublishContext";
import { useSessionContext } from "ui/context/SessionContext";
import { preparePublish } from "core/services/site/fragment";
import styles from "./ShareFacebookModal.module.scss";

const Picker = React.lazy(() => import("emoji-picker-react"));

interface Props {
    handleClose(): void;
    handleShare(
        selectedAccount: string,
        selectedPage: string,
        videoTitle: string,
        desc: string,
        status: string,
        embedding: boolean,
        category: string,
        subtitleExport: SubtitleExportValue,
    ): void;
    fragment: Fragment;
}

interface FbPage {
    id: string;
    name: string;
}

const getFacebookPages = async (
    accessToken: string,
    fragmentId: number,
    publisherKey: string,
): Promise<FbPage[]> => {
    try {
        const facebookPagesResult = await preparePublish<FbPage[]>(
            accessToken,
            fragmentId,
            publisherKey,
            "listpages",
        );
        if (facebookPagesResult.error || !facebookPagesResult.value) {
            throw new Error("get FB pages call failed");
        }
        return facebookPagesResult.value;
    } catch (err) {
        console.error(err);
        return [];
    }
};

const ShareFacebookModal: React.FC<Props> = ({
    handleClose,
    handleShare,
    fragment,
}: Props) => {
    const publishContext = usePublishContext();
    const sessionContext = useSessionContext();
    const [pages, setPages] = useState<FbPage[]>([]);
    const [selectedAccount] = useState<string | undefined>(
        publishContext.activeFacebookAccounts[0].key,
    );
    const [selectedPage, setSelectedPage] = useState<string | undefined>();
    const [videoTitle, setVideoTitle] = useState<string | undefined>(
        fragment.title,
    );
    const [desc, setDesc] = useState<string>("");
    const [status, setStatus] = useState<string>("DRAFT");
    const [embedding, setEmbedding] = useState<boolean>(true);
    const [category, setCategory] = useState<string>("ENTERTAINMENT");
    const [subtitleOption, setSubtitleOption] = useState<SubtitleExportValue>();

    const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
    const emojiElRef = useRef<HTMLDivElement | null>(null);
    const updateText = (newText: string) => {
        setDesc(newText);
    };
    const getPages = async (): Promise<void> => {
        let newPages = [...pages];
        await Promise.all(
            publishContext.activeFacebookAccounts.map(async (account) => {
                if (!sessionContext.session) {
                    return;
                }
                const result = await getFacebookPages(
                    sessionContext.session.token,
                    fragment.id,
                    account.key,
                );
                newPages = pages.concat(result);
            }),
        );
        setPages(newPages);
    };
    React.useEffect(() => {
        getPages();
    }, []);

    React.useEffect(() => {
        if (pages.length > 0) {
            setSelectedPage(pages[0].id);
        }
    }, [pages]);

    const pageOptions = pages.map((page) => ({
        id: page.id,
        title: page.name,
        value: page.id,
    }));

    const addEmoji: MouseDownEvent = (
        data: EmojiClickData,
        event: MouseEvent,
    ) => {
        const newDesc = `${desc}${data.emoji}`;
        updateText(newDesc);
    };

    const handleTitleChange = (value: string) => updateText(value);
    const toggleEmojiPicker = React.useCallback(
        (ev: React.MouseEvent<Element, MouseEvent>) => {
            ev.preventDefault();
            setShowEmojiPicker((v) => !v);
        },
        [],
    );
    const closeEmojiPicker = React.useCallback(
        () => setShowEmojiPicker(false),
        [],
    );
    useOnClickOutside(emojiElRef, closeEmojiPicker);

    const handleShareClick = () => {
        if (!selectedAccount || !selectedPage || !videoTitle) {
            return;
        }
        handleShare(
            selectedAccount,
            selectedPage,
            videoTitle,
            desc,
            status,
            embedding,
            category,
            subtitleOption,
        );
    };

    const handleSubtitleOptionChange = (val: SubtitleExportValue): void => {
        setSubtitleOption(val);
    };

    const modalContent = (
        <div className={styles.container}>
            <div className={styles.form}>
                <div className={styles.column__left}>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Post to
                        </div>
                        <Dropdown
                            theme="dark"
                            options={pageOptions}
                            onValueChanged={setSelectedPage}
                            value={selectedPage}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Video Title
                        </div>
                        <TextField
                            theme="dark"
                            onValueChanged={(value) => {
                                setVideoTitle(value);
                            }}
                            value={videoTitle || ""}
                            defaultText="Video title"
                        />
                    </div>

                    <div className={styles.fromelement}>
                        <div className={styles.formelement__caption}>
                            Description
                        </div>
                        <TextArea
                            className={styles.fbTextarea}
                            value={desc}
                            onValueChanged={handleTitleChange}
                            placeholder="What do you want to share?"
                        />
                        <div className={styles.fb__textarea__toolbar}>
                            <React.Suspense fallback={<div>Loading...</div>}>
                                <span className={styles.toggle__emoji}>
                                    <Button
                                        type="function"
                                        theme="light"
                                        tooltip="Add emoticons"
                                        onClick={(event: React.MouseEvent) =>
                                            toggleEmojiPicker(event)
                                        }
                                    >
                                        <Icon name="emoji" />
                                    </Button>
                                    <div
                                        ref={emojiElRef}
                                        className={styles.emoji__picker}
                                        style={{
                                            display: showEmojiPicker
                                                ? "block"
                                                : "none",
                                        }}
                                    >
                                        <Picker onEmojiClick={addEmoji} />
                                    </div>
                                </span>
                            </React.Suspense>
                        </div>
                    </div>
                </div>
                <div className={styles.column__right}>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Status
                        </div>
                        <Dropdown
                            theme="dark"
                            options={facebookConstants.statusOptions}
                            onValueChanged={(newValue: string) => {
                                setStatus(newValue);
                            }}
                            value={status}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>Embed</div>
                        <Dropdown
                            theme="dark"
                            options={facebookConstants.embedOptions}
                            onValueChanged={(valueString) =>
                                setEmbedding(valueString === "true")
                            }
                            value={embedding.toString()}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Category
                        </div>
                        <Dropdown
                            theme="dark"
                            options={facebookConstants.categories}
                            onValueChanged={(newValue: string) =>
                                setCategory(newValue)
                            }
                            value={category}
                        />
                    </div>
                    <div className={styles.formelement}>
                        <Tooltip
                            text="No captions for this clip"
                            disabled={fragment.hasSubtitles()}
                        >
                            <div className={styles.formelement__caption}>
                                Captions
                            </div>
                            <Dropdown
                                theme="dark"
                                options={subtitleExportOptionsBasic}
                                onValueChanged={handleSubtitleOptionChange}
                                value={subtitleOption}
                                disabled={!fragment.hasSubtitles()}
                            />
                        </Tooltip>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="facebook"
            theme="dark"
            onSave={handleShareClick}
            onClose={handleClose}
            saveButtonText="Share"
            title={`Share "${fragment.title}" to Facebook`}
            icon="facebook"
        />
    );
};

export default ShareFacebookModal;
